import { useCallback, useEffect, useState } from "react";
import { toast } from '../utils/toast';
import { fmtDateTime } from "../utils/datetime";
import PageLayout from "../components/PageLayout";
import { useAuth } from "../context/AuthContext";
import {
    getTenantDevices,
    clearUserDevice,
    UserDeviceRecord,
} from "../api/device";
import { getAppSettings } from "../api/app-settings";
import {
    Smartphone,
    Monitor,
    Search,
    Trash2,
    RefreshCw,
    CheckCircle,
    XCircle,
    Loader,
    FlaskConical,
} from "lucide-react";

interface DeviceManagementPageProps {
    onLogout?: () => void;
    onNavigate?: (page: string) => void;
}

const DeviceManagementPage: React.FC<DeviceManagementPageProps> = ({ onLogout, onNavigate }) => {
    const { user } = useAuth();
    const [devices, setDevices] = useState<UserDeviceRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [clearingId, setClearingId] = useState<number | null>(null);
    const [search, setSearch] = useState("");
    const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
    const [devMode, setDevMode] = useState(false);

    const loadDevices = useCallback(async () => {
        if (!user?.tenant_id) return;
        setLoading(true);
        try {
            const data = await getTenantDevices(user.tenant_id);
            setDevices(data);
        } catch (err) {
            console.error("Failed to load devices:", err);
        } finally {
            setLoading(false);
        }
    }, [user?.tenant_id]);

    useEffect(() => {
        loadDevices();
    }, [loadDevices]);

    useEffect(() => {
        if (!user?.tenant_id) return;
        getAppSettings(user.tenant_id)
            .then(s => setDevMode(s.dev_mode))
            .catch(() => {});
    }, [user?.tenant_id]);

    const handleClear = async (device: UserDeviceRecord) => {
        const confirmed = await toast.confirm(
            `${device.user_name ?? 'This user'} will be able to log in from a new device.`,
            'Clear device access?',
            'Clear',
        );
        if (!confirmed) return;
        setClearingId(device.id);
        try {
            await clearUserDevice(device.user_id);
            await loadDevices();
            toast.success('Device access cleared.');
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? "Failed to clear device.");
        } finally {
            setClearingId(null);
        }
    };

    const filtered = devices.filter(d => {
        const matchSearch =
            !search ||
            d.user_name?.toLowerCase().includes(search.toLowerCase()) ||
            d.user_email?.toLowerCase().includes(search.toLowerCase()) ||
            d.device_name?.toLowerCase().includes(search.toLowerCase()) ||
            d.device_type?.toLowerCase().includes(search.toLowerCase());

        const matchActive =
            filterActive === "all" ||
            (filterActive === "active" && d.is_active) ||
            (filterActive === "inactive" && !d.is_active);

        return matchSearch && matchActive;
    });

    const activeCount   = devices.filter(d => d.is_active).length;
    const inactiveCount = devices.filter(d => !d.is_active).length;


    const DeviceIcon = ({ type }: { type?: string }) => {
        const isIOS     = type?.toLowerCase().includes("ios");
        const isAndroid = type?.toLowerCase().includes("android");
        return (isIOS || isAndroid)
            ? <Smartphone className="w-4 h-4" />
            : <Monitor className="w-4 h-4" />;
    };

    return (
        <PageLayout onLogout={onLogout} currentPage="device-management" onNavigate={onNavigate}>
            <div className="p-6 max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Device Management</h1>
                        <p className="text-gray-600 mt-1">
                            Manage single-device login. Clear a user's device to let them log in from a new one.
                        </p>
                    </div>
                    <button
                        onClick={loadDevices}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 text-sm"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>

                {/* Dev-mode banner */}
                {devMode && (
                    <div className="mb-6 flex items-center gap-3 px-5 py-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                        <FlaskConical className="w-4 h-4 shrink-0 text-amber-600" />
                        <span>
                            <strong>Developer mode is active.</strong> Device clearing is disabled.
                            Go to <button onClick={() => onNavigate?.("settings")} className="underline font-medium hover:text-amber-900">App Settings</button> to switch to Production mode.
                        </span>
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {[
                        { label: "Total Records", value: devices.length,  color: "bg-gray-50  border-gray-200  text-gray-700"  },
                        { label: "Active Devices", value: activeCount,    color: "bg-green-50 border-green-200 text-green-700" },
                        { label: "Inactive",       value: inactiveCount,  color: "bg-red-50   border-red-200   text-red-700"   },
                    ].map(s => (
                        <div key={s.label} className={`border rounded-xl p-4 ${s.color}`}>
                            <p className="text-2xl font-bold">{s.value}</p>
                            <p className="text-sm mt-1">{s.label}</p>
                        </div>
                    ))}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, device..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
                            {(["all", "active", "inactive"] as const).map(f => (
                                <button
                                    key={f}
                                    onClick={() => setFilterActive(f)}
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                                        filterActive === f
                                            ? "bg-blue-600 text-white"
                                            : "text-gray-600 hover:bg-gray-100"
                                    }`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Table */}
                    {loading ? (
                        <div className="p-12 flex items-center justify-center">
                            <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                            <span className="ml-3 text-gray-500">Loading devices...</span>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            <Smartphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p>{search || filterActive !== "all" ? "No devices match the current filters." : "No device records found."}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {["Employee", "Device", "Type", "Last Login", "Status", "Actions"].map(h => (
                                            <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {filtered.map(d => (
                                        <tr key={d.id} className="hover:bg-gray-50">
                                            <td className="px-5 py-4">
                                                <p className="font-medium text-gray-900">{d.user_name ?? "—"}</p>
                                                <p className="text-xs text-gray-500">{d.user_email ?? ""}</p>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex items-center gap-2 text-gray-700">
                                                    <DeviceIcon type={d.device_type} />
                                                    <span className="text-sm">{d.device_name ?? "Unknown"}</span>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{d.device_id}</p>
                                            </td>
                                            <td className="px-5 py-4 text-sm text-gray-700">
                                                {d.device_type ?? "—"}
                                            </td>
                                            <td className="px-5 py-4 text-sm text-gray-700 whitespace-nowrap">
                                                {fmtDateTime(d.last_login_at)}
                                            </td>
                                            <td className="px-5 py-4">
                                                {d.is_active ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                        <CheckCircle className="w-3 h-3" /> Active
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                                                        <XCircle className="w-3 h-3" /> Inactive
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-4">
                                                {d.is_active ? (
                                                    <button
                                                        onClick={() => !devMode && handleClear(d)}
                                                        disabled={clearingId === d.id || devMode}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        title={devMode ? "Disabled in developer mode" : "Clear device access"}
                                                    >
                                                        {clearingId === d.id
                                                            ? <Loader className="w-3 h-3 animate-spin" />
                                                            : <Trash2 className="w-3 h-3" />}
                                                        Clear
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-gray-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Footer */}
                    {filtered.length > 0 && (
                        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                            Showing <span className="font-medium">{filtered.length}</span> of <span className="font-medium">{devices.length}</span> records
                        </div>
                    )}
                </div>
            </div>
        </PageLayout>
    );
};

export default DeviceManagementPage;
